import { pxToEm } from 'util/pxToEm';
import { formatDate } from 'util/formatDate';
import { formatTime } from 'util/formatTime';
import { formatEventLocation } from 'util/event';
import {
  generateOutlookCalendarUrl,
  generateYahooCalendarUrl,
} from 'util/calendarUrls';
import { Box, Typography, useTheme } from '@mui/material';

import React, { ReactElement, useState } from 'react';
import { Modal, ButtonLink } from 'components/ui';
import { Event } from 'services/api';
import { Image, TruncateText } from 'components/lib';

import { google, ics, CalendarEvent } from 'calendar-link';

import {
  AddToCalendarIcon,
  AppleIcon,
  CalendarIcon,
  GoogleCalendarIcon,
  OutlookIcon,
  YahooIcon,
} from 'components/icons';
import { APP_URL } from 'config/app.config';

export interface AddToCalendarButtonProps {
  event: Event;
  children: React.ReactNode;
  iconStyle?: React.CSSProperties;
  icon?: React.ComponentType<any>;
  gap?: number;
}

export const AddToCalendarButton = ({
  event,
  children,
  iconStyle,
  icon: CustomIcon,
  gap,
}: AddToCalendarButtonProps): ReactElement => {
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const theme = useTheme();
  const { name, date, startTime, endTime, flyerImageUrl } = event;

  const eventDetails = (
    <Box display="flex" mb={24} flexDirection={{ xs: 'column', md: 'row' }}>
      <Image
        src={flyerImageUrl}
        alt={name}
        height="auto"
        style={{
          marginRight: 16,
          maxWidth: 200,
        }}
      />
      <Box
        display="flex"
        justifyContent="center"
        flexDirection={'column'}
        mt={{ xs: 8, md: 'unset' }}
        maxWidth={{ xs: 'unset', md: 250 }}
      >
        <Typography
          mb={8}
          sx={{
            fontSize: pxToEm(16),
            fontWeight: 500,
          }}
        >
          {' '}
          <TruncateText lines={2}>{name}</TruncateText>
        </Typography>
        <Typography
          sx={{
            fontSize: pxToEm(12),
            fontWeight: 500,
            color: theme.palette.neutral.darkest,
            whiteSpace: 'nowrap',
          }}
        >
          {formatDate(new Date(date), {
            includeDay: true,
            includeDate: true,
            includeYear: true,
          })}
        </Typography>
        <Typography
          style={{
            fontSize: pxToEm(12),
            fontWeight: 500,
            color: theme.palette.neutral.darkest,
            whiteSpace: 'nowrap',
          }}
        >
          {startTime && formatTime(startTime)}
          {startTime && endTime && ' - '}
          {endTime && formatTime(endTime)}
        </Typography>
      </Box>
    </Box>
  );

  const location = formatEventLocation(event.setting);

  const calendarEvent = {
    title: event.name,
    start: `${date}T${startTime}`,
    end: `${date}T${endTime}`,
    description: event.description,
    location,
    url: new URL(
      event.url,
      typeof window !== 'undefined' ? window.location.href : APP_URL,
    ).href,
  } as CalendarEvent;

  const downloadICS = () => {
    const url = ics(calendarEvent);
    const anchor = document.createElement('a');
    anchor.style.display = 'none';
    anchor.href = url;
    anchor.download = `Calendar Event - ${event.name}.ics`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  };

  const addToCalendarButtons = (
    <>
      <ButtonLink
        variant="paper"
        style={{ marginRight: 16, marginBottom: 24, height: 60 }}
        onClick={downloadICS}
      >
        <Box mr={8} display="flex" alignItems={'center'} fontSize={pxToEm(30)}>
          <AppleIcon />
        </Box>
        <Typography sx={{ fontSize: '0.875em', fontWeight: 500 }}>
          Apple
        </Typography>
      </ButtonLink>

      <ButtonLink
        variant="paper"
        style={{ marginRight: 16, marginBottom: 24, height: 60 }}
        href={generateOutlookCalendarUrl(calendarEvent)}
        target="_blank"
        rel="noopener noreferrer"
      >
        <Box mr={8} display="flex" alignItems={'center'} fontSize={pxToEm(30)}>
          <OutlookIcon />
        </Box>
        <Typography sx={{ fontSize: '0.875em', fontWeight: 500 }}>
          Outlook
        </Typography>
      </ButtonLink>

      <ButtonLink
        variant="paper"
        style={{ marginRight: 16, marginBottom: 24, height: 60 }}
        href={google(calendarEvent)}
        target="_blank"
        rel="noopener noreferrer"
      >
        <Box mr={8} display="flex" alignItems={'center'} fontSize={pxToEm(30)}>
          <GoogleCalendarIcon />
        </Box>
        <Typography sx={{ fontSize: '0.875em', fontWeight: 500 }}>
          Google
        </Typography>
      </ButtonLink>

      <ButtonLink
        variant="paper"
        style={{ marginRight: 16, marginBottom: 24, height: 60 }}
        href={generateYahooCalendarUrl(calendarEvent)}
        target="_blank"
        rel="noopener noreferrer"
      >
        <Box mr={8} display="flex" alignItems={'center'} fontSize={pxToEm(30)}>
          <YahooIcon />
        </Box>
        <Typography sx={{ fontSize: '0.875em', fontWeight: 500 }}>
          Yahoo
        </Typography>
      </ButtonLink>

      <ButtonLink
        variant="paper"
        style={{ marginRight: 16, marginBottom: 24, height: 60 }}
        onClick={downloadICS}
      >
        <Box mr={8} display="flex" alignItems={'center'} fontSize={pxToEm(30)}>
          <CalendarIcon style={{ color: '#b0b0b0' }} />
        </Box>
        <Typography sx={{ fontSize: '0.875em', fontWeight: 500 }}>
          Other
        </Typography>
      </ButtonLink>
    </>
  );

  return (
    <>
      <Modal
        title="Add to your calendar"
        open={showCalendarModal}
        onClose={() => {
          setShowCalendarModal(false);
        }}
      >
        {eventDetails}
        {addToCalendarButtons}
      </Modal>
      <Box
        lineHeight={0}
        sx={{
          cursor: 'pointer',
          WebkitTapHighlightColor: 'rgba(0,0,0,0)',
          display: 'flex',
          alignItems: 'center',
          ...(gap !== undefined && { gap }),
        }}
        className="keyboardFocus"
        onClick={() => {
          setShowCalendarModal(true);
        }}
        tabIndex={0}
      >
        {CustomIcon ? (
          <CustomIcon style={iconStyle} />
        ) : (
          <AddToCalendarIcon style={iconStyle} />
        )}
        {children}
      </Box>
    </>
  );
};
